import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';

const TermsAndConditions = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [activeSection, setActiveSection] = useState(null);

  const lastUpdated = 'January 26, 2026';
  const effectiveDate = 'January 26, 2026';
  const version = '1.0';

  const sections = [
    {
      id: 'acceptance',
      title: '1. Acceptance of Terms',
      content: [
        'By accessing and using TrainaryGym ("the Platform"), you accept and agree to be bound by the terms and provisions of this agreement.',
        'If you do not agree to these Terms of Service, please do not use the Platform.',
        'We reserve the right to modify these terms at any time. Your continued use of the Platform following any changes indicates your acceptance of the new terms.'
      ]
    },
    {
      id: 'accounts',
      title: '2. User Accounts',
      content: [
        '2.1 Account Creation: You must provide accurate, complete, and current information during the registration process.',
        '2.2 Account Security: You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.',
        '2.3 Account Types: TrainaryGym offers different account types including Member, Trainer, Staff, Gym Owner, and System Administrator, each with specific access rights and responsibilities.',
        '2.4 Account Termination: We reserve the right to suspend or terminate accounts that violate these terms or engage in fraudulent activity.'
      ]
    },
    {
      id: 'membership',
      title: '3. Membership Terms',
      content: [
        '3.1 Membership Plans: Various membership plans are available with different features, pricing, and commitment periods.',
        '3.2 Auto-Renewal: Memberships automatically renew unless cancelled before the renewal date.',
        '3.3 Cancellation Policy: Members may cancel their membership with 30 days notice. Refunds are subject to the specific terms of your membership plan.',
        '3.4 Freeze Policy: Members may freeze their membership for valid reasons (medical, travel) subject to gym-specific policies.',
        '3.5 Transfer Policy: Memberships are non-transferable unless explicitly permitted by the gym owner.'
      ]
    },
    {
      id: 'payments',
      title: '4. Payment Policies',
      content: [
        '4.1 Payment Methods: We accept various payment methods including credit cards, debit cards, and digital wallets processed through Stripe.',
        '4.2 Billing Cycle: Payments are processed according to your selected billing cycle (monthly, quarterly, annually).',
        '4.3 Failed Payments: In case of payment failure, we will attempt automatic retry according to our dunning management system. Continued failure may result in membership suspension.',
        '4.4 Pricing Changes: We reserve the right to modify pricing with 30 days advance notice to existing members.',
        '4.5 Refund Policy: Refunds are processed according to the specific terms of your membership agreement and applicable consumer protection laws.'
      ]
    },
    {
      id: 'facility',
      title: '5. Facility Usage Rules',
      content: [
        '5.1 Check-In Requirements: Members must check in using their QR code or membership credentials before using facility amenities.',
        '5.2 Equipment Usage: Members must use equipment properly and follow posted safety guidelines. Report any damaged equipment immediately.',
        '5.3 Personal Training: Personal training sessions must be scheduled in advance and are subject to trainer availability.',
        '5.4 Class Bookings: Group fitness classes require advance booking. Cancellations must be made at least 2 hours before class start time.',
        '5.5 Guest Policy: Guest privileges are subject to membership plan terms and gym-specific policies.',
        '5.6 Conduct: Members must maintain respectful behavior. Harassment, intimidation, or disruptive behavior will result in immediate membership termination.'
      ]
    },
    {
      id: 'liability',
      title: '6. Liability and Assumption of Risk',
      content: [
        '6.1 Assumption of Risk: You acknowledge that physical exercise involves inherent risks including injury, disability, or death.',
        '6.2 Medical Clearance: You represent that you are physically fit to participate in fitness activities. Consult your physician before beginning any exercise program.',
        '6.3 Waiver of Liability: To the fullest extent permitted by law, TrainaryGym and participating gyms are not liable for any injuries, losses, or damages arising from facility use.',
        '6.4 Insurance: Members are encouraged to maintain adequate health and accident insurance coverage.',
        '6.5 Emergency Procedures: Staff are trained in emergency response, but TrainaryGym is not liable for medical emergencies or outcomes.'
      ]
    },
    {
      id: 'intellectual',
      title: '7. Intellectual Property',
      content: [
        '7.1 Platform Content: All content, features, and functionality on TrainaryGym are owned by us and protected by copyright, trademark, and other intellectual property laws.',
        '7.2 User Content: You retain ownership of content you submit but grant us a worldwide, non-exclusive license to use, reproduce, and display such content.',
        '7.3 Restrictions: You may not reproduce, distribute, modify, or create derivative works from Platform content without explicit permission.',
        '7.4 Trademarks: TrainaryGym and associated logos are trademarks and may not be used without written permission.'
      ]
    },
    {
      id: 'data',
      title: '8. Data Usage and Privacy',
      content: [
        '8.1 Data Collection: We collect and process personal data as described in our Privacy Policy.',
        '8.2 Fitness Data: Workout data, attendance records, and performance metrics are collected to enhance your experience.',
        '8.3 Third-Party Integrations: Data may be shared with third-party services (payment processors, communication tools) as necessary for Platform functionality.',
        '8.4 Data Security: We implement industry-standard security measures to protect your data.',
        '8.5 Your Rights: You have rights to access, correct, and delete your personal data as outlined in our Privacy Policy.'
      ]
    },
    {
      id: 'prohibited',
      title: '9. Prohibited Conduct',
      content: [
        '9.1 You may not use the Platform to engage in illegal activities or violate any applicable laws.',
        '9.2 You may not attempt to gain unauthorized access to any portion of the Platform or other users\' accounts.',
        '9.3 You may not transmit viruses, malware, or any code of a destructive nature.',
        '9.4 You may not harass, abuse, or harm other users or staff members.',
        '9.5 You may not use the Platform for commercial purposes without explicit authorization.',
        '9.6 You may not impersonate any person or entity or misrepresent your affiliation.'
      ]
    },
    {
      id: 'termination',
      title: '10. Termination',
      content: [
        '10.1 By You: You may terminate your account at any time by following the cancellation procedures.',
        '10.2 By Us: We may suspend or terminate your account immediately for violations of these terms, fraudulent activity, or non-payment.',
        '10.3 Effect of Termination: Upon termination, your right to use the Platform ceases immediately. We may delete your data subject to legal retention requirements.',
        '10.4 Survival: Provisions regarding liability, indemnification, and dispute resolution survive termination.'
      ]
    },
    {
      id: 'disclaimers',
      title: '11. Disclaimers and Warranties',
      content: [
        '11.1 AS IS Basis: The Platform is provided "as is" without warranties of any kind, either express or implied.',
        '11.2 No Guarantee: We do not guarantee that the Platform will be uninterrupted, secure, or error-free.',
        '11.3 Third-Party Services: We are not responsible for the availability or accuracy of third-party services integrated with the Platform.',
        '11.4 Fitness Results: We make no guarantees regarding fitness outcomes or health improvements.'
      ]
    },
    {
      id: 'limitation',
      title: '12. Limitation of Liability',
      content: [
        '12.1 To the maximum extent permitted by law, TrainaryGym shall not be liable for any indirect, incidental, special, consequential, or punitive damages.',
        '12.2 Our total liability shall not exceed the amount you paid for the service in the 12 months preceding the claim.',
        '12.3 Some jurisdictions do not allow limitation of liability, so these limitations may not apply to you.'
      ]
    },
    {
      id: 'indemnification',
      title: '13. Indemnification',
      content: [
        'You agree to indemnify, defend, and hold harmless TrainaryGym, its affiliates, and their respective officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses arising from:',
        '(a) Your use of the Platform',
        '(b) Your violation of these Terms',
        '(c) Your violation of any rights of another party',
        '(d) Your conduct in connection with the Platform'
      ]
    },
    {
      id: 'dispute',
      title: '14. Dispute Resolution',
      content: [
        '14.1 Informal Resolution: We encourage you to contact us first to resolve any disputes informally.',
        '14.2 Arbitration: Any disputes shall be resolved through binding arbitration rather than in court, except where prohibited by law.',
        '14.3 Class Action Waiver: You agree to resolve disputes on an individual basis and waive the right to participate in class actions.',
        '14.4 Governing Law: These Terms are governed by the laws of the jurisdiction where the Platform operator is registered.'
      ]
    },
    {
      id: 'general',
      title: '15. General Provisions',
      content: [
        '15.1 Entire Agreement: These Terms constitute the entire agreement between you and TrainaryGym.',
        '15.2 Severability: If any provision is found unenforceable, the remaining provisions remain in effect.',
        '15.3 Waiver: Our failure to enforce any right or provision does not constitute a waiver.',
        '15.4 Assignment: You may not assign these Terms without our consent. We may assign our rights without restriction.',
        '15.5 Force Majeure: We are not liable for delays or failures due to circumstances beyond our reasonable control.'
      ]
    },
    {
      id: 'contact',
      title: '16. Contact Information',
      content: [
        'For questions about these Terms of Service, please contact us:',
        'Email: legal@trainarygym.com',
        'Address: TrainaryGym Legal Department',
        'Phone: Available through your gym location',
        'Support Portal: Available in the AI Help Assistant within the Platform'
      ]
    }
  ];

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev?.[sectionId]
    }));
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Auto-expand the section
      setExpandedSections(prev => ({ ...prev, [sectionId]: true }));
    }
  };

  const filteredSections = sections?.filter(section =>
    searchQuery === '' ||
    section?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    section?.content?.some(text => text?.toLowerCase()?.includes(searchQuery?.toLowerCase()))
  );

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    // In a real implementation, this would generate a PDF
    alert('PDF download functionality would be implemented here');
  };

  return (
    <div className="min-h-screen bg-background py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors mb-4"
          >
            <Icon name="ArrowLeft" size={20} />
            <span className="text-sm font-medium">Back to Login</span>
          </Link>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-h2 font-heading text-foreground mb-2">Terms and Conditions</h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Icon name="Calendar" size={16} />
                  <span>Effective: {effectiveDate}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="RefreshCw" size={16} />
                  <span>Last Updated: {lastUpdated}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="FileText" size={16} />
                  <span>Version {version}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                icon={<Icon name="Printer" />}
                onClick={handlePrint}
              >
                Print
              </Button>
              <Button
                variant="outline"
                size="sm"
                icon={<Icon name="Download" />}
                onClick={handleDownloadPDF}
              >
                PDF
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Icon
              name="Search"
              size={20}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              type="text"
              placeholder="Search terms and conditions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Introduction */}
        <div className="card p-6 mb-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 rounded-lg bg-primary/10">
              <Icon name="Info" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h3 className="text-h5 font-heading text-foreground mb-2">Welcome to TrainaryGym</h3>
              <p className="text-body text-muted-foreground">
                These Terms and Conditions govern your use of the TrainaryGym platform and services. 
                Please read these terms carefully before using our services. By accessing or using 
                TrainaryGym, you agree to be bound by these terms.
              </p>
            </div>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="card p-6 mb-6">
          <h3 className="text-h5 font-heading text-foreground mb-4 flex items-center gap-2">
            <Icon name="List" size={20} />
            Table of Contents
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {sections?.map((section) => (
              <button
                key={section?.id}
                onClick={() => scrollToSection(section?.id)}
                className={`text-left px-3 py-2 rounded-md text-sm transition-colors ${
                  activeSection === section?.id
                    ? 'bg-primary/10 text-primary font-medium' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {section?.title}
              </button>
            ))}
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {filteredSections?.map((section) => (
            <div
              key={section?.id}
              id={section?.id}
              className="card overflow-hidden scroll-mt-4"
            >
              <button
                onClick={() => toggleSection(section?.id)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
              >
                <h3 className="text-h5 font-heading text-foreground text-left">
                  {section?.title}
                </h3>
                <Icon
                  name={expandedSections?.[section?.id] ? 'ChevronUp' : 'ChevronDown'}
                  size={20}
                  className="text-muted-foreground flex-shrink-0"
                />
              </button>

              {expandedSections?.[section?.id] && (
                <div className="px-6 pb-6 space-y-3 animate-slideDown">
                  {section?.content?.map((paragraph, index) => (
                    <p key={index} className="text-body text-muted-foreground">
                      {paragraph}
                    </p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-8 card p-6">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-accent/10">
              <Icon name="AlertCircle" size={24} color="var(--color-accent)" />
            </div>
            <div>
              <h4 className="text-h5 font-heading text-foreground mb-2">Questions?</h4>
              <p className="text-body text-muted-foreground mb-4">
                If you have any questions about these Terms and Conditions, please contact our 
                legal team or use the AI Help Assistant for immediate support.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/privacy-policy">
                  <Button variant="outline" size="sm">
                    View Privacy Policy
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="sm"
                  icon={<Icon name="Mail" />}
                  onClick={() => window.location.href = 'mailto:legal@trainarygym.com'}
                >
                  Contact Legal Team
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;